import { Box3, Mesh, Object3D } from "three";
import { ISpawnable } from "../../spawner/ISpawnable";
import { GameResourcesPackage } from "../../../../resources/GameResourcesPackage";
import { GameElement, isBox } from "../../../../models/field/GameElement";
import { ICollidable } from "../../../../../../three/physics/ICollidable";
import { BoxReward } from "./BoxReward";
import { Ticker } from "pixi.js";
import { Event } from "event-system";
import { PhysicsLayers } from "../../../../../../PhysicsLayers";
import { Physics } from "../../../../../../three/physics/Physics";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { AudioController } from "../../../AudioController";
import { GameController } from "../../../../controllers/GameController";
import { IBoxState } from "./states/IBoxState";
import { BoxSpawnState } from "./states/BoxSpawnState";
import { IDamageable } from "../../IDamageable";
import { BoxDisappearState } from "./states/BoxDisappearState";
import { BoxIdleState } from "./states/BoxIdleState";
import { BatchedRenderer } from "three.quarks";

export class Box extends Object3D implements ICollidable, ISpawnable, IDamageable {
    public readonly isDamageable: boolean = true; 
    protected currentState: IBoxState;
    public readonly reward: BoxReward;
    public readonly onCanBeDestroyed: Event<ISpawnable, void, void, void, void, void>;
    protected readonly box: Mesh;
    private readonly parachute: Object3D;
    protected readonly _boundingBox: Box3;
    public get isLifetimeOver(): boolean {
        return this.lifeTimeMS === 0;
    }
    public get health(): number {
        return this._health;
    }
    public get alive(): boolean {
        return this._health > 0;
    }
    constructor(
        protected readonly gameResources: GameResourcesPackage,
        protected readonly gameController: GameController,
        protected readonly audioController: AudioController,
        protected readonly particlesRenderer: BatchedRenderer,
        public readonly type: GameElement,
        public readonly fieldIndex: number,
        protected lifeTimeMS: number,
        protected _health: number = 1
    ) {
        super();
        if(!isBox(type)) {
            throw new Error(`BaseBox try to create Box with type=${type}`);
        }
        this.onCanBeDestroyed = new Event();
        this.reward = new BoxReward(type);
        switch (type) {
            case GameElement.BoosterBox:
                this.box = clone(gameResources.assets_game_Box_glb.scene.children[0]) as Mesh;
                break;
            case GameElement.HealBox:
                this.box = clone(gameResources.assets_game_Box_glb.scene.children[1]) as Mesh;
                break;
            case GameElement.WeaponsBox:
                this.box = clone(gameResources.assets_game_Box_glb.scene.children[2]) as Mesh;
                break;
        }
        this.parachute = clone(gameResources.assets_game_Parachute_glb.scene.children[0]);
        this._boundingBox = new Box3();
        this.add(this.box, this.parachute);
        this.parachute.scale.set(4, 4, 4);
        this.position.set(0, 6, 0);

        const disappearState = new BoxDisappearState(this.gameResources, this, this.audioController, this.particlesRenderer);
        const idleState = new BoxIdleState(this, disappearState);
        this.switchState(new BoxSpawnState(this.gameResources, this.particlesRenderer, this, idleState));
       
        Physics.add(this);
        Ticker.shared.add(this.onTick, this);
    }

    public get boundingBox(): Box3 {
        return this._boundingBox.copy(this.box.geometry.boundingBox).applyMatrix4(this.matrixWorld);
    }

    public get collidesWithLayer(): number {
        return 1 << PhysicsLayers.Layer1;
    }

    public takeDamage(amount: number): void {
        if (this._health > 0) {
            this._health = Math.max(this._health - amount, 0);
            this.gameController.setBoxReward(this.reward, this.fieldIndex);
        }
    }

    protected switchState(nextState: IBoxState): void {
        this.currentState?.exit();
        const previousState = this.currentState;
        this.currentState = nextState;
        this.currentState.enter(previousState);
    }

    protected onTick(): void {
        const nextState = this.currentState.process(Ticker.shared.deltaMS);
        if (nextState != this.currentState) {
            if (nextState != null) {
                this.switchState(nextState);
            } else {
                this.onCanBeDestroyed.emit(this);
            }
        }
        this.lifeTimeMS = Math.max(this.lifeTimeMS - Ticker.shared.deltaMS, 0);
    }

    public removeParachute(): void {
        if (this.parachute == null) {
            console.warn("Parachute already removed");
            return;
        }
        this.remove(this.parachute);
    }

    public destroy(): void {
        Physics.remove(this);
        Ticker.shared.remove(this.onTick, this);
    }
}